import React from 'react';
import { motion } from 'framer-motion';
import { LockIcon } from 'lucide-react';
import { PinGateState } from '../hooks/usePinGate';
interface PinGateProps {
  gate: PinGateState;
}
export function PinGate({
  gate
}: PinGateProps) {
  if (gate.pinGateInitializing) {
    return <div role="status" className="flex min-h-screen flex-col items-center justify-center gap-3 text-sm text-slate-500" data-id="element-411" data-mp="2dawvu907k2ox">
        <span aria-hidden="true" className="h-8 w-8 animate-spin rounded-full border-[3px] border-slate-200 border-t-brand-600" data-id="element-412" data-mp="28bttwxfiycl6" />
        <p data-id="element-413" data-mp="23j0kvxd0wi3a">Checking dashboard access…</p>
      </div>;
  }
  if (gate.pinGateOpen) {
    return <>
        <div className="fixed inset-0 z-40 bg-slate-900/50" aria-hidden="true" data-id="element-414" data-mp="1ukf6meel3p8b" />
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-415" data-mp="1uic8t876wrt1">
          <motion.section role="dialog" aria-modal="true" aria-labelledby="commission-pin-title" initial={{
          opacity: 0,
          y: 12,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} transition={{
          duration: 0.18,
          ease: 'easeOut'
        }} className="w-full max-w-sm rounded-2xl bg-white p-8 text-center shadow-xl" data-id="element-416" data-mp="1pkas2tx8l4kq">
            <div aria-hidden="true" className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-brand-700" data-id="element-417" data-mp="3abj9218s8gux">
              <LockIcon className="h-5 w-5" data-id="element-418" data-mp="6xghgzyf6oxi" />
            </div>
            <h2 id="commission-pin-title" className="mt-4 text-lg font-bold text-slate-900" data-id="element-419" data-mp="bw9xaz16is0u">
              {gate.pinModalTitle}
            </h2>
            <p className="mt-1.5 text-sm text-slate-500" data-id="element-420" data-mp="3t3vmpk4vz4kd">{gate.pinModalSubtitle}</p>

            <form onSubmit={event => {
            event.preventDefault();
            if (!gate.pinSubmitDisabled) gate.submitCommissionPin();
          }} data-id="element-421" data-mp="1w99azsd7nqb">
              <div className="mt-6 space-y-4 text-left" data-id="element-422" data-mp="2mpmj8ge5w3zu">
                <PinField id="commission-pin-value-input" label="Enter PIN" ariaLabel="Commission dashboard PIN" value={gate.pinValue} onChange={gate.handlePinInput} autoFocus data-id="element-423" data-mp="t5ubsi53v5yd" />
                {gate.isPinCreateMode ? <PinField id="commission-pin-confirm-input" label="Confirm PIN" ariaLabel="Confirm commission dashboard PIN" value={gate.pinConfirmValue} onChange={gate.handlePinConfirmInput} data-id="element-424" data-mp="efwr4c6dwydx" /> : null}
              </div>

              {gate.pinError ? <p role="alert" className="mt-4 text-sm font-semibold text-red-600" data-id="element-425" data-mp="3roj2mn6du3tv">
                  {gate.pinError}
                </p> : null}

              <div className="mt-6 grid grid-cols-2 gap-3" data-id="element-426" data-mp="1pv3ebwxrp122">
                <button type="button" onClick={gate.closePinModal} disabled={gate.isVerifyingPin} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60" data-id="element-427" data-mp="1ptik68fd7k4q">
                  Cancel
                </button>
                <button type="submit" disabled={gate.pinSubmitDisabled} className="rounded-lg bg-brand-700 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-800 disabled:cursor-not-allowed disabled:bg-brand-300" data-id="element-428" data-mp="1eg9u0y2s6emz">
                  {gate.pinSubmitLabel}
                </button>
              </div>
            </form>
          </motion.section>
        </div>
      </>;
  }
  if (gate.isLockedOut) {
    return <div className="flex min-h-screen items-center justify-center p-6" data-id="element-429" data-mp="34sfqeb4nyi7p">
        <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-8 text-center shadow-card" data-id="element-430" data-mp="34uiozjjvtjo1">
          <div aria-hidden="true" className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500" data-id="element-431" data-mp="2ycfkr80p9sud">
            <LockIcon className="h-5 w-5" data-id="element-432" data-mp="3ca4245vk6huo" />
          </div>
          <h2 className="mt-4 text-lg font-bold text-slate-900" data-id="element-433" data-mp="37c2klpds6qlb">Dashboard locked</h2>
          <p className="mt-1.5 text-sm text-slate-500" data-id="element-434" data-mp="3lcdfr4m4irca">
            Your commission details stay hidden until you enter your PIN.
          </p>
          <button type="button" onClick={gate.reopenPinModal} className="mt-6 w-full rounded-lg bg-brand-700 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-800" data-id="element-435" data-mp="3hb1lglsb1hxr">
            Enter PIN
          </button>
        </div>
      </div>;
  }
  return null;
}
interface PinFieldProps {
  id: string;
  label: string;
  ariaLabel: string;
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
}
function PinField({
  id,
  label,
  ariaLabel,
  value,
  onChange,
  autoFocus = false
}: PinFieldProps) {
  return <div data-id="element-436" data-mp="rj8vmiqqzcxe">
      <label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-slate-500" data-id="element-437" data-mp="rizbvcwaq205">
        {label}
      </label>
      <input id={id} type="password" inputMode="numeric" pattern="[0-9]*" maxLength={4} autoComplete="off" autoFocus={autoFocus} value={value} onChange={event => onChange(event.target.value)} aria-label={ariaLabel} className="mt-1.5 w-full rounded-lg border border-slate-300 px-4 py-3 text-center text-2xl font-bold tracking-[0.6em] text-slate-900 focus:border-brand-600" data-id="element-438" data-mp="iiu6t9ekrfp5" />
    </div>;
}